'use client';

import { useEffect, useMemo, useState } from 'react';

import { useRouter, useSearchParams } from 'next/navigation';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

import { Button } from '@/components/ui/button';

import { Checkbox } from '@/components/ui/checkbox';

import { Label } from '@/components/ui/label';

import { useSetupProfile } from '@/hooks/useSetupProfile';

import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';

import { ChevronDown } from 'lucide-react';
import Link from 'next/link';

interface SideFilterProps {
  selectedFilters: {
    areaOfInterestIds: string[];
    industryIds: string[];
    occupationIds: string[];
  };
  setSelectedFilters: (filters: {
    areaOfInterestIds: string[];
    industryIds: string[];
    occupationIds: string[];
  }) => void;
}

export const SideFilter = ({
  setSelectedFilters,
  selectedFilters,
}: SideFilterProps) => {
  const { getCategoriesApi, getOccupationApi, getIndustryApi } =
    useSetupProfile({
      loadCategories: true,
      loadOccupation: true,
      loadIndustry: true,
    });

  // Type for grouped occupation parent
  type OccupationParent = {
    id: string;
    name: string;
    subOccupations: { id: string; name: string }[];
  };

  const router = useRouter();

  const searchParams = useSearchParams();

  const [openCategories, setOpenCategories] = useState<string[]>([
    'Areas of Interest',
    'Occupation',
    'Industry',
  ]);

  const isLoading =
    getCategoriesApi.isPending ||
    getOccupationApi.isPending ||
    getIndustryApi.isPending;

  // Initialize selected filters from URL on component mount

  // useEffect(() => {
  //   const areaOfInterestFromUrl =
  //     searchParams.get('areaOfInterest')?.split(',') || [];
  //   const industryFromUrl = searchParams.get('industry')?.split(',') || [];
  //   const occupationFromUrl = searchParams.get('occupation')?.split(',') || [];

  //   setSelectedFilters({
  //     areaOfInterestIds: areaOfInterestFromUrl,
  //     industryIds: industryFromUrl,
  //     occupationIds: occupationFromUrl,
  //   });
  // }, [searchParams]);

  // Set open categories when data is loaded

  useEffect(() => {
    if (getCategoriesApi.data?.data) {
      const categoryKeys = Object.keys(getCategoriesApi.data.data).sort((a, b) =>
        a.localeCompare(b)
      );

      // Get the first two category keys if they exist

      const firstTwoKeys = categoryKeys.slice(0, 2);

      setOpenCategories(firstTwoKeys);
    }
  }, [getCategoriesApi.data]);

  const sortedCategoryEntries = useMemo(() => {
    const data = getCategoriesApi.data?.data;
    if (!data) return [] as [string, { id: string; name: string }[]][];
    return Object.entries(data)
      .map(
        ([key, value]) =>
          [key, [...value].sort((a, b) => a.name.localeCompare(b.name))] as [
            string,
            { id: string; name: string }[],
          ]
      )
      .sort(([a], [b]) => a.localeCompare(b));
  }, [getCategoriesApi.data]);

  const sortedOccupationParents = useMemo(() => {
    const raw = getOccupationApi.data?.data;
    if (!Array.isArray(raw) || raw.length === 0) return [] as OccupationParent[];
    return [...(raw as OccupationParent[])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((parent) => ({
        ...parent,
        subOccupations: [...parent.subOccupations].sort((x, y) =>
          x.name.localeCompare(y.name)
        ),
      }));
  }, [getOccupationApi.data]);

  const sortedIndustryParents = useMemo(() => {
    const raw = getIndustryApi.data?.data;
    if (!Array.isArray(raw) || raw.length === 0)
      return [] as {
        id: string;
        name: string;
        subIndustries: { id: string; name: string }[];
      }[];
    return [
      ...(raw as {
        id: string;
        name: string;
        subIndustries: { id: string; name: string }[];
      }[]),
    ]
      .filter((parent) => parent.subIndustries.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((parent) => ({
        ...parent,
        subIndustries: [...parent.subIndustries].sort((x, y) =>
          x.name.localeCompare(y.name)
        ),
      }));
  }, [getIndustryApi.data]);

  // Update URL when filters change

  // const updateUrlWithFilters = (
  //   areaOfInterest: string[],
  //   industry: string[],
  //   occupation: string[]
  // ) => {
  //   const params = new URLSearchParams(searchParams.toString());

  //   if (areaOfInterest.length > 0) {
  //     params.set('areaOfInterest', areaOfInterest.join(','));
  //   } else {
  //     params.delete('areaOfInterest');
  //   }

  //   if (industry.length > 0) {
  //     params.set('industry', industry.join(','));
  //   } else {
  //     params.delete('industry');
  //   }

  //   if (occupation.length > 0) {
  //     params.set('occupation', occupation.join(','));
  //   } else {
  //     params.delete('occupation');
  //   }

  //   router.push(`?${params.toString()}`);
  // };

  // Handle checkbox change

  const handleFilterChange = (
    filterId: string,
    checked: boolean,
    filterType: 'areaOfInterest' | 'industry' | 'occupation'
  ) => {
    const currentFilters = { ...selectedFilters };
    const targetArray =
      filterType === 'areaOfInterest'
        ? currentFilters.areaOfInterestIds
        : filterType === 'industry'
          ? currentFilters.industryIds
          : currentFilters.occupationIds;

    const newFilters = checked
      ? [...targetArray, filterId]
      : targetArray.filter((id) => id !== filterId);

    const updatedFilters = {
      ...currentFilters,
      [filterType === 'areaOfInterest'
        ? 'areaOfInterestIds'
        : filterType === 'industry'
          ? 'industryIds'
          : 'occupationIds']: newFilters,
    };

    setSelectedFilters(updatedFilters);
    // updateUrlWithFilters(
    //   updatedFilters.areaOfInterestIds,
    //   updatedFilters.industryIds,
    //   updatedFilters.occupationIds
    // );
  };

  // Handle select all for a category
  const handleSelectAll = (
    categoryItems: { id: string }[],
    checked: boolean,
    filterType: 'areaOfInterest' | 'industry' | 'occupation'
  ) => {
    const currentFilters = { ...selectedFilters };
    const targetArray =
      filterType === 'areaOfInterest'
        ? currentFilters.areaOfInterestIds
        : filterType === 'industry'
          ? currentFilters.industryIds
          : currentFilters.occupationIds;

    const newFilters = checked
      ? [...targetArray, ...categoryItems.map((item) => item.id)]
      : targetArray.filter(
          (id) => !categoryItems.some((item) => item.id === id)
        );

    const updatedFilters = {
      ...currentFilters,
      [filterType === 'areaOfInterest'
        ? 'areaOfInterestIds'
        : filterType === 'industry'
          ? 'industryIds'
          : 'occupationIds']: newFilters,
    };

    setSelectedFilters(updatedFilters);
    // updateUrlWithFilters(
    //   updatedFilters.areaOfInterestIds,
    //   updatedFilters.industryIds,
    //   updatedFilters.occupationIds
    // );
  };

  // Check if all items in a category are selected
  const areAllItemsSelected = (
    categoryItems: { id: string }[],
    filterType: 'areaOfInterest' | 'industry' | 'occupation'
  ) => {
    const targetArray =
      filterType === 'areaOfInterest'
        ? selectedFilters.areaOfInterestIds
        : filterType === 'industry'
          ? selectedFilters.industryIds
          : selectedFilters.occupationIds;

    return categoryItems.every((item) => targetArray.includes(item.id));
  };

  // Check if some items in a category are selected
  const areSomeItemsSelected = (
    categoryItems: { id: string }[],
    filterType: 'areaOfInterest' | 'industry' | 'occupation'
  ) => {
    const targetArray =
      filterType === 'areaOfInterest'
        ? selectedFilters.areaOfInterestIds
        : filterType === 'industry'
          ? selectedFilters.industryIds
          : selectedFilters.occupationIds;
    return categoryItems.some((item) => targetArray.includes(item.id));
  };

  // Clear all filters

  const handleClearAll = () => {
    const emptyFilters = {
      areaOfInterestIds: [],
      industryIds: [],
      occupationIds: [],
    };
    setSelectedFilters(emptyFilters);
    // updateUrlWithFilters([], [], []);
  };

  return (
    <aside className="sticky top-20 self-start py-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-normal">Filters</h2>

        <Button
          variant="ghost"
          className="text-xs hover:bg-ihs-blue/20"
          size="sm"
          onClick={handleClearAll}
        >
          Clear All
        </Button>
      </div>

      <div className="pr-4 max-h-[75vh] overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="h-6 w-full mb-2" />
            ))}
          </div>
        ) : (
          <Accordion
            type="multiple"
            className="w-full"
            defaultValue={[]}
            onValueChange={setOpenCategories}
          >
            {/* Areas of Interest Accordion */}
            <AccordionItem className="border-none" value="Area of Interest">
              <AccordionTrigger
                isChevron
                className="text-sm justify-between w-full"
              >
                Area of Interest
              </AccordionTrigger>
              <AccordionContent>
                <div className="overflow-y-auto w-full">
                  {sortedCategoryEntries.map(([key, value]) => (
                      <div key={key} className="mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Checkbox
                            id={`select-all-${key}`}
                            checked={areAllItemsSelected(
                              value,
                              'areaOfInterest'
                            )}
                            indeterminate={
                              !areAllItemsSelected(value, 'areaOfInterest') &&
                              areSomeItemsSelected(value, 'areaOfInterest')
                            }
                            onCheckedChange={(checked) =>
                              handleSelectAll(
                                value,
                                checked === true,
                                'areaOfInterest'
                              )
                            }
                          />
                          <Label
                            htmlFor={`select-all-${key}`}
                            className="text-sm font-medium"
                          >
                            {key}
                          </Label>
                        </div>
                        <div className="flex flex-col gap-4 py-2 px-4">
                          {value.map((item) => (
                            <div
                              key={item.id}
                              className="flex items-center gap-2"
                            >
                              <Checkbox
                                id={`area-${item.id}`}
                                checked={selectedFilters.areaOfInterestIds.includes(
                                  item.id
                                )}
                                onCheckedChange={(checked) =>
                                  handleFilterChange(
                                    item.id,
                                    checked === true,
                                    'areaOfInterest'
                                  )
                                }
                              />
                              <Label htmlFor={`area-${item.id}`}>
                                {item.name}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Roles Accordion */}
            <AccordionItem className="border-none" value="Roles">
              <AccordionTrigger
                isChevron
                className="text-sm justify-between w-full"
              >
                Job Role
              </AccordionTrigger>
              <AccordionContent>
                <div className="overflow-y-auto w-full">
                  {/* Grouped Occupations: parent with children */}
                  {sortedOccupationParents.length > 0 ? (
                    sortedOccupationParents.map((parent) => (
                        <div key={parent.id} className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Checkbox
                              id={`select-all-occupation-${parent.id}`}
                              checked={areAllItemsSelected(
                                parent.subOccupations,
                                'occupation'
                              )}
                              indeterminate={
                                !areAllItemsSelected(
                                  parent.subOccupations,
                                  'occupation'
                                ) &&
                                areSomeItemsSelected(
                                  parent.subOccupations,
                                  'occupation'
                                )
                              }
                              onCheckedChange={(checked) =>
                                handleSelectAll(
                                  parent.subOccupations,
                                  checked === true,
                                  'occupation'
                                )
                              }
                            />
                            <Label
                              htmlFor={`select-all-occupation-${parent.id}`}
                              className="text-sm font-medium"
                            >
                              {parent.name}
                            </Label>
                          </div>
                          <div className="flex flex-col gap-4 py-2 px-4">
                            {parent.subOccupations.map(
                              (item: { id: string; name: string }) => (
                                <div
                                  key={item.id}
                                  className="flex items-center gap-2"
                                >
                                  <Checkbox
                                    id={`occupation-${item.id}`}
                                    checked={selectedFilters.occupationIds.includes(
                                      item.id
                                    )}
                                    onCheckedChange={(checked) =>
                                      handleFilterChange(
                                        item.id,
                                        checked === true,
                                        'occupation'
                                      )
                                    }
                                  />
                                  <Label htmlFor={`occupation-${item.id}`}>
                                    {item.name}
                                  </Label>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 px-4 py-2">
                      No roles found.
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Industry Accordion - Grouped by Parent */}
            <AccordionItem className="border-none" value="Industry">
              <AccordionTrigger
                isChevron
                className="text-sm justify-between w-full"
              >
                Industry
              </AccordionTrigger>
              <AccordionContent>
                <div className="overflow-y-auto w-full">
                  {/* Grouped Industries: parent with children */}
                  {sortedIndustryParents.length > 0 ? (
                    sortedIndustryParents.map((parent) => (
                        <div key={parent.id} className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Checkbox
                              id={`select-all-industry-${parent.id}`}
                              checked={areAllItemsSelected(
                                parent.subIndustries,
                                'industry'
                              )}
                              indeterminate={
                                !areAllItemsSelected(
                                  parent.subIndustries,
                                  'industry'
                                ) &&
                                areSomeItemsSelected(
                                  parent.subIndustries,
                                  'industry'
                                )
                              }
                              onCheckedChange={(checked) =>
                                handleSelectAll(
                                  parent.subIndustries,
                                  checked === true,
                                  'industry'
                                )
                              }
                            />
                            <Label
                              htmlFor={`select-all-industry-${parent.id}`}
                              className="text-sm font-medium"
                            >
                              {parent.name}
                            </Label>
                          </div>
                          <div className="flex flex-col gap-4 py-2 px-4">
                            {parent.subIndustries.map(
                              (item: { id: string; name: string }) => (
                                <div
                                  key={item.id}
                                  className="flex items-center gap-2"
                                >
                                  <Checkbox
                                    id={`industry-${item.id}`}
                                    checked={selectedFilters.industryIds.includes(
                                      item.id
                                    )}
                                    onCheckedChange={(checked) =>
                                      handleFilterChange(
                                        item.id,
                                        checked === true,
                                        'industry'
                                      )
                                    }
                                  />
                                  <Label htmlFor={`industry-${item.id}`}>
                                    {item.name}
                                  </Label>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500 px-4 py-2">
                      No industries found.
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>

      <div className="my-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            href="/privacy-policy"
            className="text-sm hover:underline text-gray-500"
          >
            Privacy Policy
          </Link>
          <div className="w-1 h-1 bg-gray-500 rounded-full" />
          <Link
            href="/terms-of-service"
            className="text-sm hover:underline text-gray-500"
          >
            Terms of Service
          </Link>
          <div className="w-1 h-1 bg-gray-500 rounded-full" />

          <Link
            href="https://docs.google.com/forms/d/e/1FAIpQLSfL3-2QInAeQtWo_vQxhTtqLpbM3ENp2CUgUKyv_F2fvmGU0A/viewform"
            className="text-sm hover:underline text-gray-500"
            target="_blank"
          >
            Feedback Form
          </Link>
          <div className="w-1 h-1 bg-gray-500 rounded-full" />

          {/* dropdown with two more options Help Center and FAQs  */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <span className="text-sm text-gray-500 flex items-center gap-1">
                Help/Support
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </span>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white w-40 p-2 rounded-md shadow-md">
              <DropdownMenuItem className="text-sm mb-2 text-gray-500">
                <Link
                  href="/contact"
                  target="_blank"
                  className="hover:underline"
                >
                  Help Center
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem className="text-sm mb-2 text-gray-500">
                <Link href="/faqs" target="_blank" className="hover:underline">
                  FAQs
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </aside>
  );
};
